/**
 * Buyer-side same-origin fetch helper (TNK-167 phase C).
 *
 * The buyer session rides an HttpOnly cookie (`tcg-web.buyer_session`)
 * that the browser attaches automatically — never visible to JS, matching
 * the seller-side pattern in `apiClient.ts`. Mutating requests echo the
 * readable buyer CSRF cookie back in `X-CSRF-Token` (double-submit).
 *
 * Kept separate from `apiClient.apiFetch` because the cookie pair is
 * distinct: seller routes echo `tcg-web.csrf`, buyer routes echo
 * `tcg-web.buyer_csrf`. A single client that tries to read whichever is
 * present picks the wrong one when an operator who's also paired as a
 * buyer is browsing `/shop` — the seller CSRF cookie wins by accident
 * and the buyer-gated request 403s.
 */
const BUYER_CSRF_COOKIE = "tcg-web.buyer_csrf";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

function readBuyerCsrfToken(): string {
  try {
    const prefix = `${BUYER_CSRF_COOKIE}=`;
    const row = document.cookie.split("; ").find((r) => r.startsWith(prefix));
    return row ? decodeURIComponent(row.slice(prefix.length)) : "";
  } catch {
    return "";
  }
}

export async function buyerFetch(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  const method = (init?.method ?? "GET").toUpperCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readBuyerCsrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  return fetch(url, { ...init, headers });
}
